import { useCallback, useEffect, useRef, useState } from 'react'
import { NetworkWithAscii } from '@/lib/viz/NetworkWithAscii'
import { TextChunk, generateSimpleEmbeddings } from '@/lib/viz/embeddings'
import { useRepresentationStore } from '@/store/representationStore'
import { LoadingText, loadingPresets } from '@/lib/loadingAnimations'
import { theme } from '@/lib/theme'
import useIsMobile from '@/hooks/useIsMobile'

interface WorkingRepresentationAsciiVizProps {
  workingRepresentation: any
  userId: string
  width: number
  height: number
  colors: Record<string, string>
  growthSpeed?: 'slow' | 'medium' | 'fast'
  charSize?: number
  helpEnabled?: boolean
  isActive?: boolean
}

// Common stop words to filter out (from original Representation.tsx)
const STOP_WORDS = new Set([
  'the',
  'be',
  'to',
  'of',
  'and',
  'a',
  'in',
  'that',
  'have',
  'i',
  'it',
  'for',
  'not',
  'on',
  'with',
  'he',
  'as',
  'you',
  'do',
  'at',
  'this',
  'but',
  'his',
  'by',
  'from',
  'they',
  'we',
  'say',
  'her',
  'she',
  'or',
  'an',
  'will',
  'my',
  'one',
  'all',
  'would',
  'there',
  'their',
  'what',
  'so',
  'up',
  'out',
  'if',
  'about',
  'who',
  'get',
  'which',
  'go',
  'me',
  'when',
  'make',
  'can',
  'like',
  'time',
  'no',
  'just',
  'him',
  'know',
  'take',
  'people',
  'into',
  'year',
  'your',
  'good',
  'some',
  'could',
  'them',
  'see',
  'other',
  'than',
  'then',
  'now',
  'look',
  'only',
  'come',
  'its',
  'over',
  'think',
  'also',
  'back',
  'after',
  'use',
  'two',
  'how',
  'our',
  'work',
  'first',
  'well',
  'way',
  'even',
  'new',
  'want',
  'because',
  'any',
  'these',
  'give',
  'day',
  'most',
  'us',
  'is',
  'was',
  'are',
  'been',
  'has',
  'had',
  'were',
  'said',
  'did',
  'having',
  'may',
  'should',
  'am',
  'utc',
  'stated',
  'wants',
  'asked',
])

interface ChunkWithId extends TextChunk {
  id: string
  type: 'explicit' | 'deductive'
}

/**
 * Gets the user's name from the cached peer card
 */
function getUserNameFromPeerCard(): string | null {
  const PEER_CARD_CACHE_KEY = 'profile_peer_card_cache'

  try {
    const cachedRaw = localStorage.getItem(PEER_CARD_CACHE_KEY)
    if (!cachedRaw) return null

    const cached = JSON.parse(cachedRaw)
    const peerCard = cached.value
    if (!peerCard) return null

    // Extract Name field from peer card (format: "Name: John Doe\n...")
    const lines = peerCard.split('\n')
    for (const line of lines) {
      const trimmed = line.trim()
      if (trimmed.toLowerCase().startsWith('name:')) {
        const name = trimmed.substring(5).trim() // Remove "Name:" prefix
        return name || null
      }
    }
  } catch (error) {
    console.error('Error extracting name from peer card:', error)
  }

  return null
}

/**
 * Extract meaningful chunks from the working representation
 */
function extractMeaningfulClusters(
  representation: any,
  userId: string,
  registerNode?: (id: string, text: string, type: 'explicit' | 'deductive') => void
): ChunkWithId[] {
  const chunks: ChunkWithId[] = []

  // Get user's name from peer card to replace UUID with name
  const userName = getUserNameFromPeerCard()
  const replacementName = userName || 'User' // Fallback to 'User' if no name found

  // Helper to clean and filter text
  const cleanText = (text: string) => {
    // Replace UUID with the user's actual name
    return text
      .replace(new RegExp(userId, 'gi'), replacementName)
      .trim()
      .replace(/\s+/g, ' ')
  }

  // Helper to filter out stop words
  const hasSignificantContent = (phrase: string) => {
    const words = phrase.toLowerCase().split(/\s+/)
    const significantWords = words.filter((w) => !STOP_WORDS.has(w) && w.length > 2)
    return significantWords.length >= 1
  }

  // Helper to create stable hash from content
  const hashContent = (text: string): string => {
    let hash = 0
    for (let i = 0; i < text.length; i++) {
      const char = text.charCodeAt(i)
      hash = ((hash << 5) - hash) + char
      hash = hash & hash // Convert to 32-bit integer
    }
    return Math.abs(hash).toString(36)
  }

  // Extract from explicit content
  if (representation?.representation?.explicit) {
    representation.representation.explicit.forEach((item: any) => {
      if (item.content) {
        const cleaned = cleanText(item.content)
        if (cleaned && hasSignificantContent(cleaned)) {
          const id = `explicit-${hashContent(cleaned)}`
          chunks.push({
            text: cleaned,
            size: 4,
            id,
            type: 'explicit',
          })
          // Register with store
          if (registerNode) {
            registerNode(id, cleaned, 'explicit')
          }
        }
      }
    })
  }

  // Extract from deductive conclusions
  if (representation?.representation?.deductive) {
    representation.representation.deductive.forEach((item: any) => {
      if (item.conclusion) {
        const cleaned = cleanText(item.conclusion)
        if (cleaned && hasSignificantContent(cleaned)) {
          const id = `deductive-${hashContent(cleaned)}`
          chunks.push({
            text: cleaned,
            size: 10,
            id,
            type: 'deductive',
          })
          // Register with store
          if (registerNode) {
            registerNode(id, cleaned, 'deductive')
          }
        }
      }
      // Also include premises
      if (item.premises && Array.isArray(item.premises)) {
        item.premises.forEach((premise: string) => {
          const cleaned = cleanText(premise)
          if (cleaned && hasSignificantContent(cleaned)) {
            const id = `premise-${hashContent(cleaned)}`
            chunks.push({
              text: cleaned,
              size: 1,
              id,
              type: 'deductive',
            })
            // Register premises as deductive type
            if (registerNode) {
              registerNode(id, cleaned, 'deductive')
            }
          }
        })
      }
    })
  }

  // Deduplicate and aggregate
  const chunkMap = new Map<string, ChunkWithId>()
  chunks.forEach((chunk) => {
    const normalized = chunk.text.toLowerCase()
    const existing = chunkMap.get(normalized)
    if (existing) {
      existing.size += chunk.size
    } else {
      chunkMap.set(normalized, { ...chunk })
    }
  })

  // Convert back to array and sort by importance
  const result = Array.from(chunkMap.values())
    .sort((a, b) => b.size - a.size)
    .slice(0, 50) // Take top 50

  return result
}

export default function WorkingRepresentationAsciiViz({
  workingRepresentation,
  userId,
  width,
  height,
  colors,
  growthSpeed = 'medium',
  charSize = 10,
  helpEnabled = false,
  isActive = true,
}: WorkingRepresentationAsciiVizProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const overlayCanvasRef = useRef<HTMLCanvasElement>(null)
  const networkRef = useRef<NetworkWithAscii | null>(null)
  const animationFrameRef = useRef<number | null>(null)
  const isActiveRef = useRef(isActive)
  const isMobile = useIsMobile()
  const [paused, setPaused] = useState(false)
  const [overlayEnabled, setOverlayEnabled] = useState(true)
  const [isHovered, setIsHovered] = useState(false)
  const [textVisible, setTextVisible] = useState(!isMobile) // Default to false on mobile
  const [nodesVisible, setNodesVisible] = useState(true)
  const [linesVisible, setLinesVisible] = useState(true)
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null)
  const [showHelp, setShowHelp] = useState(false)
  
  // Navigation state for semantic browsing - tracks current position in sorted neighbor list
  const neighborIndexRef = useRef<number>(0)
  const sortedNeighborsRef = useRef<number[]>([])
  
  // Tour order for organic navigation - precomputed path through all nodes
  const tourOrderRef = useRef<number[]>([])
  const tourIndexRef = useRef<number>(0)
  
  // Draggable fact window state for mobile
  const [factWindowPosition, setFactWindowPosition] = useState<{ x: number; y: number } | null>(null)
  const [isDraggingFactWindow, setIsDraggingFactWindow] = useState(false)
  const dragStartRef = useRef<{ x: number; y: number; startX: number; startY: number } | null>(null)
  const factWindowRef = useRef<HTMLDivElement>(null)

  // Zustand store for node tracking
  const {
    registerNode,
    getNodeColor,
    isNodeNew,
    hasProcessedChunk,
    addProcessedChunks,
    getNormalizationBounds,
    setNormalizationBounds,
    vizTextChunks,
    setVizTextChunks,
    vizEmbeddingsHighD,
    appendVizEmbeddingsHighD,
    vizEmbeddings2D,
    setVizEmbeddings2DReplace,
  } = useRepresentationStore()

  // Store embeddings and chunks in refs so they persist
  const textChunksRef = useRef<ChunkWithId[]>([])
  const embeddings2DRef = useRef<Array<[number, number]>>([])
  const embeddingsHighDRef = useRef<number[][]>([]) // High-D embeddings storage
  const umapWorkerRef = useRef<Worker | null>(null)
  const workerReadyRef = useRef<boolean>(false)
  const pendingEmbeddingsRef = useRef<{ embeddings: number[][], startIndex: number } | null>(null)
  const lastProcessedCountRef = useRef<number>(0)
  const lastCanvasWRef = useRef<number>(width)
  const lastCanvasHRef = useRef<number>(height)

  const getPadding = (w: number, h: number) => {
    // Reduce padding on mobile to give more spread for nodes
    if (isMobile) {
      const base = Math.round(Math.min(w, h) * 0.02)
      return Math.max(8, Math.min(24, base))
    }
    const base = Math.round(Math.min(w, h) * 0.05)
    return Math.max(16, Math.min(60, base))
  }

  // Compute tour order using greedy nearest-neighbor algorithm
  // This creates a path that travels organically through the visualization
  const computeTourOrder = (embeddings: Array<[number, number]>): number[] => {
    if (embeddings.length === 0) return []
    if (embeddings.length === 1) return [0]
    
    const visited = new Set<number>()
    const tour: number[] = []
    
    // Start from node 0
    let current = 0
    tour.push(current)
    visited.add(current)
    
    // Greedily visit nearest unvisited node
    while (visited.size < embeddings.length) {
      const currentEmb = embeddings[current]
      let nearestDist = Infinity
      let nearestIdx = -1
      
      for (let i = 0; i < embeddings.length; i++) {
        if (visited.has(i)) continue
        const emb = embeddings[i]
        const dist = Math.sqrt(
          Math.pow(emb[0] - currentEmb[0], 2) + 
          Math.pow(emb[1] - currentEmb[1], 2)
        )
        if (dist < nearestDist) {
          nearestDist = dist
          nearestIdx = i
        }
      }
      
      if (nearestIdx !== -1) {
        tour.push(nearestIdx)
        visited.add(nearestIdx)
        current = nearestIdx
      }
    }
    
    return tour
  }

  // Recompute tour when embeddings change
  useEffect(() => {
    const embeddings = embeddings2DRef.current
    if (embeddings.length > 0) {
      tourOrderRef.current = computeTourOrder(embeddings)
      // Reset tour index to start
      tourIndexRef.current = 0
    }
  }, [vizEmbeddings2D]) // Trigger when store embeddings change

  // Keep isActiveRef in sync
  useEffect(() => {
    isActiveRef.current = isActive
  }, [isActive])

  // Sync textVisible with isMobile on mount/change and clear hover on mobile
  useEffect(() => {
    setTextVisible(!isMobile)
    // Ensure hover target is always null on mobile
    if (isMobile && networkRef.current) {
      networkRef.current.setHoveredTarget(null)
    }
  }, [isMobile])

  // Close fact dialog when help is enabled
  useEffect(() => {
    if (helpEnabled && selectedIndex !== null) {
      setSelectedIndex(null)
    }
  }, [helpEnabled])

  // Navigate tour in given direction (-1 for previous, +1 for next)
  const navigateTour = useCallback((direction: number) => {
    const tour = tourOrderRef.current
    if (tour.length === 0) return
    const currentTourIdx = selectedIndex !== null ? tour.indexOf(selectedIndex) : -1
    if (currentTourIdx === -1) {
      setSelectedIndex(tour[0])
      tourIndexRef.current = 0
      return
    }
    const newTourIdx = (currentTourIdx + direction + tour.length) % tour.length
    tourIndexRef.current = newTourIdx
    setSelectedIndex(tour[newTourIdx])
  }, [selectedIndex])

  // Drag handlers for mobile fact window
  const handleFactWindowDragStart = (e: React.TouchEvent) => {
    if (!isMobile) return
    // Don't start drag if touching a button
    const target = e.target as HTMLElement
    if (target.tagName === 'BUTTON' || target.closest('button')) return
    
    // Stop propagation to prevent carousel swipe
    e.stopPropagation()
    
    const touch = e.touches[0]
    const currentPos = factWindowPosition || { x: 0, y: 0 }
    dragStartRef.current = {
      x: currentPos.x,
      y: currentPos.y,
      startX: touch.clientX,
      startY: touch.clientY,
    }
    setIsDraggingFactWindow(true)
  }

  const handleFactWindowDrag = (e: React.TouchEvent) => {
    if (!isDraggingFactWindow || !dragStartRef.current) return
    
    // Stop propagation to prevent carousel swipe
    e.stopPropagation()
    // Note: Don't call preventDefault() - touch events are passive by default
    // touchAction: 'none' on the element handles scroll prevention
    
    const touch = e.touches[0]
    const deltaX = touch.clientX - dragStartRef.current.startX
    const deltaY = touch.clientY - dragStartRef.current.startY
    
    // Calculate new position
    let newX = dragStartRef.current.x + deltaX
    let newY = dragStartRef.current.y + deltaY
    
    // Get bounds - keep window within screen with 8px padding
    const factWindow = factWindowRef.current
    if (factWindow) {
      const windowRect = factWindow.getBoundingClientRect()
      const containerWidth = width
      const containerHeight = height
      const padding = 8
      
      // Calculate the default position (bottom: 8, left: 8, right: 8)
      const defaultLeft = 8
      const defaultBottom = 8
      const windowWidth = windowRect.width
      const windowHeight = windowRect.height
      
      // Calculate bounds for the transform offset
      // Max left offset: can't go past right edge (with padding)
      const maxX = containerWidth - defaultLeft - windowWidth - padding
      // Max right offset (negative): can't go past left edge (with padding)
      const minX = -(defaultLeft - padding)
      // Max up offset (negative): can go up to top with padding (accounting for search bar ~50px)
      const minY = -(containerHeight - defaultBottom - windowHeight - padding)
      // Max down offset: can't go below bottom (with padding)
      const maxY = defaultBottom - padding
      
      // Clamp position within bounds
      newX = Math.max(minX, Math.min(maxX, newX))
      newY = Math.max(minY, Math.min(maxY, newY))
    }
    
    setFactWindowPosition({ x: newX, y: newY })
  }

  const handleFactWindowDragEnd = (e: React.TouchEvent) => {
    // Stop propagation to prevent carousel swipe
    e.stopPropagation()
    
    setIsDraggingFactWindow(false)
    dragStartRef.current = null
  }

  // Handle tap on canvas to enter selection mode on mobile
  const handleCanvasTap = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isMobile || !networkRef.current) return
    
    // Always clear hover target on mobile to prevent floating text
    networkRef.current.setHoveredTarget(null)
    
    const canvas = overlayCanvasRef.current
    if (!canvas) return
    
    const rect = canvas.getBoundingClientRect()
    const clientX = 'touches' in e ? e.touches[0]?.clientX : e.clientX
    const clientY = 'touches' in e ? e.touches[0]?.clientY : e.clientY
    
    if (clientX === undefined || clientY === undefined) return
    
    const x = clientX - rect.left
    const y = clientY - rect.top
    
    // Find nearest node
    const id = networkRef.current.getNearestTargetId(x, y, 50)
    if (id) {
      const idx = textChunksRef.current.findIndex(c => c.id === id)
      if (idx !== -1) {
        // Compute sorted neighbors for this node
        const currentEmb = embeddings2DRef.current[idx]
        if (currentEmb) {
          const neighbors = embeddings2DRef.current
            .map((emb, i) => ({
              idx: i,
              dist: Math.sqrt(Math.pow(emb[0] - currentEmb[0], 2) + Math.pow(emb[1] - currentEmb[1], 2))
            }))
            .filter(n => n.idx !== idx)
            .sort((a, b) => a.dist - b.dist)
            .map(n => n.idx)
          sortedNeighborsRef.current = neighbors
          neighborIndexRef.current = 0
        }
        setSelectedIndex(idx)
      }
    }
  }

  // Rehydrate from store on mount
  useEffect(() => {
    if (vizTextChunks.length > 0) {
      textChunksRef.current = vizTextChunks as ChunkWithId[]
    }
    if (vizEmbeddings2D.length > 0) {
      embeddings2DRef.current = vizEmbeddings2D
    }
    if (vizEmbeddingsHighD.length > 0) {
      embeddingsHighDRef.current = vizEmbeddingsHighD
    }
  }, [vizTextChunks.length, vizEmbeddings2D.length, vizEmbeddingsHighD.length])

  // Initialize projection worker (UMAP fit/transform with fallback)
  useEffect(() => {
    // Module worker built by Vite
    const worker = new Worker(new URL('@/lib/viz/umapWorker.ts', import.meta.url), { type: 'module' })
    umapWorkerRef.current = worker

    umapWorkerRef.current.onmessage = (e) => {
      if (e.data.type === 'ready') {
        workerReadyRef.current = true
        if (pendingEmbeddingsRef.current) {
          umapWorkerRef.current?.postMessage({
            type: 'fit',
            embeddings: pendingEmbeddingsRef.current.embeddings,
            startIndex: pendingEmbeddingsRef.current.startIndex,
          })
          pendingEmbeddingsRef.current = null
        }
      } else if (e.data.type === 'fitted') {
        const raw2D = e.data.embeddings2D as Array<[number, number]>
        const startIndex = e.data.startIndex || 0

        const xs = raw2D.map((p) => p[0])
        const ys = raw2D.map((p) => p[1])

        const padding = getPadding(width, height)

        const currentBounds = getNormalizationBounds()
        // Helper to center horizontally when content doesn't occupy canvas width
        const centerHorizontally = (pts: Array<[number, number]>) => {
          const pad = padding
          if (pts.length === 0) return pts
          const minX = Math.min(...pts.map((p) => p[0]))
          const maxX = Math.max(...pts.map((p) => p[0]))
          const contentWidth = maxX - minX
          const available = width - 2 * pad
          // Center if the occupied width is significantly smaller than available
          if (contentWidth <= available * 0.85) {
            const center = pad + available / 2
            const currentCenter = (minX + maxX) / 2
            const dx = Math.round(center - currentCenter)
            return pts.map(([x, y]) => [
              Math.max(pad, Math.min(width - pad, x + dx)),
              y,
            ]) as Array<[number, number]>
          }
          return pts
        }
        if (!currentBounds) {
          const nb = {
            minX: Math.min(...xs),
            maxX: Math.max(...xs),
            minY: Math.min(...ys),
            maxY: Math.max(...ys),
          }
          setNormalizationBounds(nb)
          const rangeX = nb.maxX - nb.minX || 1
          const rangeY = nb.maxY - nb.minY || 1
          const normalized2D = raw2D.map((p) => {
            let nx = padding + ((p[0] - nb.minX) / rangeX) * (width - 2 * padding)
            let ny = padding + ((p[1] - nb.minY) / rangeY) * (height - 2 * padding)
            nx = Math.max(padding, Math.min(width - padding, nx))
            ny = Math.max(padding, Math.min(height - padding, ny))
            return [nx, ny] as [number, number]
          })
          const centered = centerHorizontally(normalized2D)
          setVizEmbeddings2DReplace(centered)
          embeddings2DRef.current = centered
        } else {
          // Expand bounds to include new points if necessary
          const expanded = {
            minX: Math.min(currentBounds.minX, Math.min(...xs)),
            maxX: Math.max(currentBounds.maxX, Math.max(...xs)),
            minY: Math.min(currentBounds.minY, Math.min(...ys)),
            maxY: Math.max(currentBounds.maxY, Math.max(...ys)),
          }

          const changed =
            expanded.minX !== currentBounds.minX ||
            expanded.maxX !== currentBounds.maxX ||
            expanded.minY !== currentBounds.minY ||
            expanded.maxY !== currentBounds.maxY

          if (changed) {
            setNormalizationBounds(expanded)

            // Denormalize existing normalized points back to raw using previous bounds
            const prevRangeX = currentBounds.maxX - currentBounds.minX || 1
            const prevRangeY = currentBounds.maxY - currentBounds.minY || 1
            const prevRaw = embeddings2DRef.current.map((pt) => {
              const rx = currentBounds.minX + ((pt[0] - padding) / (width - 2 * padding)) * prevRangeX
              const ry = currentBounds.minY + ((pt[1] - padding) / (height - 2 * padding)) * prevRangeY
              return [rx, ry] as [number, number]
            })

            const combinedRaw = [...prevRaw, ...raw2D]
            const rangeX = expanded.maxX - expanded.minX || 1
            const rangeY = expanded.maxY - expanded.minY || 1
            const renorm = combinedRaw.map((p) => {
              let nx = padding + ((p[0] - expanded.minX) / rangeX) * (width - 2 * padding)
              let ny = padding + ((p[1] - expanded.minY) / rangeY) * (height - 2 * padding)
              nx = Math.max(padding, Math.min(width - padding, nx))
              ny = Math.max(padding, Math.min(height - padding, ny))
              return [nx, ny] as [number, number]
            })
            const centered = centerHorizontally(renorm)
            setVizEmbeddings2DReplace(centered)
            embeddings2DRef.current = centered
          } else {
            const rangeX = currentBounds.maxX - currentBounds.minX || 1
            const rangeY = currentBounds.maxY - currentBounds.minY || 1
            const normalized2D = raw2D.map((p) => {
              let nx = padding + ((p[0] - currentBounds.minX) / rangeX) * (width - 2 * padding)
              let ny = padding + ((p[1] - currentBounds.minY) / rangeY) * (height - 2 * padding)
              nx = Math.max(padding, Math.min(width - padding, nx))
              ny = Math.max(padding, Math.min(height - padding, ny))
              return [nx, ny] as [number, number]
            })
            const combined = [...embeddings2DRef.current, ...normalized2D]
            const centered = centerHorizontally(combined)
            setVizEmbeddings2DReplace(centered)
            embeddings2DRef.current = centered
          }
        }
      }
    }

    return () => {
      umapWorkerRef.current?.terminate()
    }
  }, [width, height])

  // Sync theme colors and visibility flags into network at runtime
  useEffect(() => {
    if (networkRef.current) {
      networkRef.current.setColors(colors)
      networkRef.current.setKnnOverlayEnabled(overlayEnabled && linesVisible)
      networkRef.current.setTextVisible(textVisible)
      networkRef.current.setNodesVisible(nodesVisible)
      networkRef.current.setSelectedTargetIndex(selectedIndex)
    }
  }, [colors, overlayEnabled, textVisible, nodesVisible, linesVisible, selectedIndex])

  // Extract chunks and generate embeddings when representation changes
  useEffect(() => {
    if (!workingRepresentation) return

    const allChunks = extractMeaningfulClusters(workingRepresentation, userId, registerNode)

    if (allChunks.length === 0) {
      return
    }

    // Filter to only NEW chunks we haven't processed yet
    const newChunks = allChunks.filter(chunk => !hasProcessedChunk(chunk.id))

    // If no new chunks, ensure we still have something to render
    if (newChunks.length === 0) {
      // If we already have embeddings/chunks in memory or store, do nothing
      if (textChunksRef.current.length > 0 && embeddings2DRef.current.length > 0) {
        return
      }

      // Otherwise, use all current chunks to bootstrap the viz (e.g., after refresh)
      const bootstrapChunks = allChunks.slice(0, 50)
      textChunksRef.current = bootstrapChunks
      setVizTextChunks(bootstrapChunks)

      const bootstrapEmbeddings = generateSimpleEmbeddings(bootstrapChunks)
      embeddingsHighDRef.current = bootstrapEmbeddings
      appendVizEmbeddingsHighD(bootstrapEmbeddings)

      const startIndex = 0
      if (umapWorkerRef.current && workerReadyRef.current) {
        umapWorkerRef.current.postMessage({
          type: 'fit',
          embeddings: bootstrapEmbeddings,
          startIndex,
        })
      } else {
        pendingEmbeddingsRef.current = { embeddings: bootstrapEmbeddings, startIndex }
      }
      return
    }

    // Log for debugging

    // Add new chunk IDs to processed set
    addProcessedChunks(newChunks.map(c => c.id))

    // Check if this is the first batch
    const isFirstBatch = textChunksRef.current.length === 0

    // Append new chunks to existing ones (incremental growth!)
    const updatedChunks = [...textChunksRef.current, ...newChunks].slice(0, 50) // Limit to 50 total
    textChunksRef.current = updatedChunks
    setVizTextChunks(updatedChunks)

    // Generate embeddings ONLY for NEW chunks (not all!)
    const newEmbeddings = generateSimpleEmbeddings(newChunks)

    // Append to high-D embeddings storage
    embeddingsHighDRef.current = [...embeddingsHighDRef.current, ...newEmbeddings]
    appendVizEmbeddingsHighD(newEmbeddings)

    // Send to worker for projection with startIndex
    const startIndex = isFirstBatch ? 0 : embeddings2DRef.current.length


    if (umapWorkerRef.current && workerReadyRef.current) {
      umapWorkerRef.current.postMessage({
        type: 'fit',
        embeddings: newEmbeddings,
        startIndex: startIndex
      })
    } else {
      pendingEmbeddingsRef.current = { embeddings: newEmbeddings, startIndex }
    }

    lastProcessedCountRef.current = allChunks.length
  }, [workingRepresentation, userId, registerNode])

  // Initialize canvas and animation loop
  useEffect(() => {
    const canvas = canvasRef.current
    const overlay = overlayCanvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    const overlayCtx = overlay ? overlay.getContext('2d') : null
    if (!ctx) return

    // Standard canvas setup (NO HiDPI for pixelated look)
    canvas.width = width
    canvas.height = height

    // HiDPI setup for overlay (crisp text/labels)
    if (overlay && overlayCtx) {
      const dpr = (window.devicePixelRatio || 1)
      overlay.width = Math.floor(width * dpr)
      overlay.height = Math.floor(height * dpr)
      overlay.style.width = `${width}px`
      overlay.style.height = `${height}px`
      overlayCtx.setTransform(dpr, 0, 0, dpr, 0, 0)
      // Disable image smoothing on overlay just in case (text unaffected but rect edges crisper)
      ;(overlayCtx as any).imageSmoothingEnabled = false
    }

    // Character dimensions
    const charWidth = charSize * 0.6
    const charHeight = charSize

    // Create network
    if (!networkRef.current) {
      networkRef.current = new NetworkWithAscii({
        width,
        height,
        charWidth,
        charHeight,
        colors,
        getNodeColor,
        isNodeNew,
      })

      // Set growth speed (FASTER for more dynamic visualization)
      const speedMap = { slow: 3, medium: 2, fast: 1 }
      networkRef.current.setGrowthInterval(speedMap[growthSpeed])
    }

    let lastTime = performance.now()
    let lastFrameTime = 0
    // Throttle to ~20fps on mobile for battery/performance, 30fps on desktop
    const targetFrameTime = isMobile ? 50 : 33

    // Animation loop with throttling and visibility check
    const animate = (currentTime: number) => {
      if (!networkRef.current || !ctx) return

      // Skip if page is hidden (tab not visible) or component is not active (e.g., not the active mobile window)
      if (document.hidden || !isActiveRef.current) {
        animationFrameRef.current = requestAnimationFrame(animate)
        return
      }

      // Throttle frame rate
      const elapsed = currentTime - lastFrameTime
      if (elapsed < targetFrameTime) {
        animationFrameRef.current = requestAnimationFrame(animate)
        return
      }
      lastFrameTime = currentTime

      const deltaTime = currentTime - lastTime
      lastTime = currentTime

      const embeddings = embeddings2DRef.current
      const textChunks = textChunksRef.current

      // Clear with background
      ctx.fillStyle = colors.secondaryBg || '#0a0a0a'
      ctx.fillRect(0, 0, width, height)
      // Clear overlay completely each frame
      if (overlayCtx) overlayCtx.clearRect(0, 0, width, height)

      // Don't render if no data yet (loading overlay shown by parent)
      if (embeddings.length === 0 || textChunks.length === 0) {
        animationFrameRef.current = requestAnimationFrame(animate)
        return
      }

      // Update and draw network (pass embeddings, chunks, and stable IDs)
      const chunkIds = textChunks.map(chunk => (chunk as ChunkWithId).id || `chunk-${textChunks.indexOf(chunk)}`)
      if (!paused) {
      networkRef.current.update(embeddings, textChunks, deltaTime, chunkIds)
      }
      networkRef.current.draw(ctx, overlayCtx || undefined)

      animationFrameRef.current = requestAnimationFrame(animate)
    }

    animate(performance.now())

    return () => {
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current)
      }
    }
  }, [width, height, colors, growthSpeed, charSize, paused, isMobile])

  // Re-normalize points when canvas size changes to keep everything fitting and centered
  useEffect(() => {
    const bounds = getNormalizationBounds()
    if (!bounds) {
      lastCanvasWRef.current = width
      lastCanvasHRef.current = height
      return
    }

    if (embeddings2DRef.current.length === 0) {
      lastCanvasWRef.current = width
      lastCanvasHRef.current = height
      return
    }

    const prevW = lastCanvasWRef.current
    const prevH = lastCanvasHRef.current
    if (prevW === width && prevH === height) return

    const prevPad = getPadding(prevW, prevH)
    const newPad = getPadding(width, height)
    const rangeX = bounds.maxX - bounds.minX || 1
    const rangeY = bounds.maxY - bounds.minY || 1

    // Denormalize using previous size
    const raw = embeddings2DRef.current.map((pt) => {
      const rx = bounds.minX + ((pt[0] - prevPad) / (prevW - 2 * prevPad)) * rangeX
      const ry = bounds.minY + ((pt[1] - prevPad) / (prevH - 2 * prevPad)) * rangeY
      return [rx, ry] as [number, number]
    })

    // Normalize to new size
    const renorm = raw.map((p) => {
      let nx = newPad + ((p[0] - bounds.minX) / rangeX) * (width - 2 * newPad)
      let ny = newPad + ((p[1] - bounds.minY) / rangeY) * (height - 2 * newPad)
      nx = Math.max(newPad, Math.min(width - newPad, nx))
      ny = Math.max(newPad, Math.min(height - newPad, ny))
      return [nx, ny] as [number, number]
    })

    // Center horizontally if there is spare room
    const pad = newPad
    const minX = Math.min(...renorm.map((p) => p[0]))
    const maxX = Math.max(...renorm.map((p) => p[0]))
    const contentW = maxX - minX
    const avail = width - 2 * pad
    let centered = renorm
    if (contentW <= avail * 0.85) {
      const center = pad + avail / 2
      const currentCenter = (minX + maxX) / 2
      const dx = Math.round(center - currentCenter)
      centered = renorm.map(([x, y]) => [
        Math.max(pad, Math.min(width - pad, x + dx)),
        y,
      ]) as Array<[number, number]>
    }

    setVizEmbeddings2DReplace(centered)
    embeddings2DRef.current = centered

    lastCanvasWRef.current = width
    lastCanvasHRef.current = height
  }, [width, height])

  // Keyboard controls with selection mode
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!networkRef.current) return

      // Ignore when an input/textarea/contenteditable element is focused (but NOT in selection mode)
      const ae = document.activeElement as HTMLElement | null
      if (ae && (ae.tagName === 'INPUT' || ae.tagName === 'TEXTAREA' || ae.isContentEditable) && selectedIndex === null) {
        return
      }

      // Selection mode - arrow keys to navigate facts (only when modal already open on desktop)
      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        // On desktop, only allow navigation if modal is already open
        // Don't open modal with arrow keys on desktop
        if (selectedIndex === null) {
          return // Don't intercept, let window management handle it
        }
        
        e.preventDefault() // Prevent window management
        e.stopPropagation()

        const totalChunks = textChunksRef.current.length
        if (totalChunks === 0) return

        // Navigate through facts
        if (e.key === 'ArrowLeft') {
          setSelectedIndex((prev) => (prev === null ? 0 : (prev - 1 + totalChunks) % totalChunks))
        } else {
          setSelectedIndex((prev) => (prev === null ? 0 : (prev + 1) % totalChunks))
        }
        return
      }

      // Escape exits selection mode OR clears trails
      if (e.key === 'Escape') {
        if (selectedIndex !== null) {
          e.preventDefault()
          e.stopPropagation()
          setSelectedIndex(null)
        } else {
          networkRef.current.clearTrails()
        }
        return
      }

      // Help toggle
      if (e.key === '?') {
        e.preventDefault()
        setShowHelp((v) => !v)
        return
      }

      // Other controls only work when NOT in selection mode or when hovered

      // Space only when mouse is over the canvas
      if ((e.key === ' ' || e.code === 'Space')) {
        if (!isHovered) return
        e.preventDefault()
        setPaused((p) => !p)
      } else if (e.key.toLowerCase() === 'x') {
        setPaused((p) => !p)
      } else if (e.key === '+' || e.key === '=') {
        networkRef.current.setGrowthInterval(1)
      } else if (e.key === '-') {
        networkRef.current.setGrowthInterval(4)
      } else if (e.key.toLowerCase() === 'k' || e.key.toLowerCase() === 'o') {
        setOverlayEnabled((v) => !v)
      } else if (e.key.toLowerCase() === 't') {
        setTextVisible((v) => !v)
      } else if (e.key.toLowerCase() === 'n') {
        setNodesVisible((v) => !v)
      } else if (e.key.toLowerCase() === 'l') {
        setLinesVisible((v) => !v)
      }
    }
    window.addEventListener('keydown', onKey, { capture: true })
    return () => window.removeEventListener('keydown', onKey, { capture: true })
  }, [isHovered, selectedIndex])

  // Click to spawn manual origin toward nearest target
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas || !networkRef.current) return
    const onClick = (e: MouseEvent) => {
      const rect = canvas.getBoundingClientRect()
      const x = e.clientX - rect.left
      const y = e.clientY - rect.top
      networkRef.current!.spawnManualOrigin(x, y)
    }
    canvas.addEventListener('click', onClick)
    return () => canvas.removeEventListener('click', onClick)
  }, [])

  // Hover to enlarge and show label (disabled in selection mode and on mobile)
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas || !networkRef.current) return
    
    // Disable hover effects on mobile - never show floating text
    if (isMobile) {
      networkRef.current.setHoveredTarget(null)
      return
    }
    
    const onMove = (e: MouseEvent) => {
      // Don't update hover in selection mode
      if (selectedIndex !== null) return

      const rect = canvas.getBoundingClientRect()
      const x = e.clientX - rect.left
      const y = e.clientY - rect.top
      const id = networkRef.current!.getNearestTargetId(x, y, 36)
      networkRef.current!.setHoveredTarget(id)
    }
    const onLeave = () => {
      if (selectedIndex === null) {
        networkRef.current!.setHoveredTarget(null)
      }
      setIsHovered(false)
    }
    const onEnter = () => setIsHovered(true)
    canvas.addEventListener('mousemove', onMove)
    canvas.addEventListener('mouseleave', onLeave)
    canvas.addEventListener('mouseenter', onEnter)
    return () => {
      canvas.removeEventListener('mousemove', onMove)
      canvas.removeEventListener('mouseleave', onLeave)
      canvas.removeEventListener('mouseenter', onEnter)
    }
  }, [selectedIndex, isMobile])

  // Show loading state if we have a representation but embeddings aren't ready yet
  const isProcessing = workingRepresentation && (embeddings2DRef.current.length === 0 || textChunksRef.current.length === 0)

  return (
    <div style={{ position: 'relative', width: `${width}px`, height: `${height}px` }}>
      {isProcessing && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10,
          }}
        >
          <LoadingText
            {...loadingPresets.thinking('Loading representation...', colors.primaryAccent || colors.primaryText)}
            fontSize={isMobile ? '12px' : '14px'}
          />
        </div>
      )}

      {/* Help Overlay */}
      {showHelp && (
        <div
          style={{
            position: 'absolute',
            top: 16,
            left: 16,
            backgroundColor: colors.primaryBg,
            border: `1px solid ${colors.primaryBorder}`,
            borderRadius: '4px',
            padding: '12px 16px',
            zIndex: 20,
            fontFamily: theme.fontFamily.mono,
            fontSize: '11px',
            lineHeight: '1.6',
            color: colors.primaryText,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
            maxWidth: '320px',
          }}
        >
          <div style={{ fontWeight: 'bold', marginBottom: '8px', color: colors.primaryAccent }}>
            Keyboard Shortcuts
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 12px' }}>
            <span style={{ color: colors.primaryAccent }}>?</span>
            <span>Toggle this help</span>

            <span style={{ color: colors.primaryAccent }}>«/»</span>
            <span>Navigate facts (selection mode)</span>

            <span style={{ color: colors.primaryAccent }}>ESC</span>
            <span>Exit selection mode / Clear trails</span>

            <span style={{ color: colors.primaryAccent }}>T</span>
            <span>Toggle text labels</span>

            <span style={{ color: colors.primaryAccent }}>N</span>
            <span>Toggle nodes/trails</span>

            <span style={{ color: colors.primaryAccent }}>L</span>
            <span>Toggle connection lines</span>

            <span style={{ color: colors.primaryAccent }}>Space</span>
            <span>Pause/resume animation</span>

            <span style={{ color: colors.primaryAccent }}>X</span>
            <span>Pause/resume animation</span>

            <span style={{ color: colors.primaryAccent }}>+/-</span>
            <span>Speed up/slow down growth</span>

            <span style={{ color: colors.primaryAccent }}>K/O</span>
            <span>Toggle kNN overlay</span>
          </div>
        </div>
      )}

      {/* Mobile Browse Nodal Points Button */}
      {isMobile && selectedIndex === null && textChunksRef.current.length > 0 && (
        <button
          onClick={() => {
            // Initialize sorted neighbors from first node
            const firstEmb = embeddings2DRef.current[0]
            if (firstEmb) {
              const neighbors = embeddings2DRef.current
                .map((emb, i) => ({
                  idx: i,
                  dist: Math.sqrt(Math.pow(emb[0] - firstEmb[0], 2) + Math.pow(emb[1] - firstEmb[1], 2))
                }))
                .filter(n => n.idx !== 0)
                .sort((a, b) => a.dist - b.dist)
                .map(n => n.idx)
              sortedNeighborsRef.current = neighbors
              neighborIndexRef.current = 0
            }
            setSelectedIndex(0)
          }}
          style={{
            position: 'absolute',
            bottom: 16,
            right: 16,
            padding: '10px 16px',
            backgroundColor: colors.primaryAccent,
            border: 'none',
            borderRadius: '20px',
            fontSize: '11px',
            fontWeight: '600',
            color: '#fff',
            cursor: 'pointer',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
            zIndex: 15,
          }}
        >
          Browse Context
        </button>
      )}

      {/* Selection Mode Indicator */}
      {selectedIndex !== null && textChunksRef.current[selectedIndex] && (
        <div
          ref={factWindowRef}
          onTouchStart={isMobile ? handleFactWindowDragStart : undefined}
          onTouchMove={isMobile ? handleFactWindowDrag : undefined}
          onTouchEnd={isMobile ? handleFactWindowDragEnd : undefined}
          style={{
            position: 'absolute',
            bottom: isMobile ? 8 : 16,
            left: isMobile ? 8 : 16,
            right: isMobile ? 8 : 16,
            zIndex: 20,
            transform: isMobile && factWindowPosition ? `translate(${factWindowPosition.x}px, ${factWindowPosition.y}px)` : undefined,
            touchAction: isMobile ? 'none' : undefined,
          }}
        >
          {/* Close button tab - positioned outside but connected to the main box */}
          <button
            onClick={() => setSelectedIndex(null)}
            style={{
              position: 'absolute',
              top: '-24px',
              right: '18px',
              padding: '4px 10px 8px 10px',
              backgroundColor: colors.primaryBg,
              border: `2px solid ${colors.primaryAccent}`,
              borderBottom: 'none',
              borderRadius: '6px 6px 0 0',
              fontSize: '12px',
              color: colors.secondaryText,
              cursor: 'pointer',
              lineHeight: 1,
              zIndex: 22,
            }}
          >
            ✕
          </button>
          {/* Cover strip to hide border where tab meets box */}
          <div
            style={{
              position: 'absolute',
              top: '2px',
              right: '20px',
              width: '28px',
              height: '2px',
              backgroundColor: colors.primaryBg,
              zIndex: 23,
            }}
          />
          
          {/* Main content box */}
          <div
            style={{
              backgroundColor: colors.primaryBg,
              border: `2px solid ${colors.primaryAccent}`,
              borderRadius: '8px',
              padding: isMobile ? '14px 16px' : '12px 16px',
              fontFamily: theme.fontFamily.mono,
              fontSize: isMobile ? '12px' : '12px',
              lineHeight: '1.5',
              color: colors.primaryText,
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
              cursor: isMobile ? 'grab' : undefined,
              minHeight: isMobile ? '50px' : undefined,
            }}
          >
            {/* Drag handle for mobile */}
            {isMobile && (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  marginBottom: '6px',
                  marginTop: '-4px',
                }}
              >
                <div
                  style={{
                    width: '32px',
                    height: '4px',
                    backgroundColor: colors.primaryBorder,
                    borderRadius: '2px',
                  }}
                />
              </div>
            )}
            {/* Desktop hint */}
            {!isMobile && (
              <div style={{ textAlign: 'right', marginBottom: '8px' }}>
                <span style={{ fontSize: '10px', color: colors.secondaryText }}>
                  « » to navigate • ESC to exit
                </span>
              </div>
            )}
          {/* Content with navigation floated to bottom right for mobile */}
          {isMobile ? (
            <div style={{ color: colors.primaryText }}>
              {/* Float container for buttons */}
              <span style={{ 
                float: 'right',
                display: 'flex', 
                gap: '6px',
                marginLeft: '10px',
                marginBottom: '4px',
              }}>
                <button
                  onClick={() => navigateTour(-1)}
                  style={{
                    backgroundColor: colors.secondaryBg,
                    border: 'none',
                    borderRadius: '4px',
                    padding: '6px 12px',
                    fontSize: '16px',
                    color: colors.primaryText,
                    cursor: 'pointer',
                  }}
                >
                  «
                </button>
                <button
                  onClick={() => navigateTour(1)}
                  style={{
                    backgroundColor: colors.secondaryBg,
                    border: 'none',
                    borderRadius: '4px',
                    padding: '6px 12px',
                    fontSize: '16px',
                    color: colors.primaryText,
                    cursor: 'pointer',
                  }}
                >
                  »
                </button>
              </span>
              {(textChunksRef.current[selectedIndex] as ChunkWithId).text}
              {/* Extra padding for short facts on mobile */}
              {(textChunksRef.current[selectedIndex] as ChunkWithId).text.length < 60 && (
                <div style={{ paddingBottom: '16px' }} />
              )}
            </div>
          ) : (
            <div style={{ color: colors.primaryText }}>
              {(textChunksRef.current[selectedIndex] as ChunkWithId).text}
            </div>
          )}
          </div>
        </div>
      )}

      <canvas
        ref={canvasRef}
        style={{
          width: `${width}px`,
          height: `${height}px`,
          imageRendering: 'pixelated',
          WebkitFontSmoothing: 'none',
          MozOsxFontSmoothing: 'grayscale',
        }}
      />
      <canvas
        ref={overlayCanvasRef}
        onClick={isMobile ? handleCanvasTap : undefined}
        onTouchStart={isMobile ? handleCanvasTap : undefined}
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          width: `${width}px`,
          height: `${height}px`,
          pointerEvents: isMobile ? 'auto' : 'none',
          touchAction: isMobile ? 'manipulation' : 'auto',
        }}
      />
    </div>
  )
}
